import LoadingComponent from '@/view/shared/LoadingComponent';
import React from 'react';

interface LoadableOptions {
  loader: () => Promise<any>;
}

/**
 * Custom lazy loading wrapper with loading component
 * Uses shadcn/ui Loading component for the fallback
 * 
 * @example
 * const MyLazyComponent = CustomLoadable({
 *   loader: () => import('./MyComponent')
 * });
 */
export default function CustomLoadable(opts: LoadableOptions): React.FC<any> {
  const LazyComponent = React.lazy(async () => {
    try {
      const module = await opts.loader();
      // Handle both default and named exports
      return { default: module.default || module };
    } catch (error) {
      console.error('Error loading component:', error);
      // Return a fallback component in case of error
      return {
        default: () => (
          <div className="flex h-full items-center justify-center p-4 text-destructive">
            <p>Error loading component. Please try again.</p>
          </div>
        ),
      };
    }
  });

  return function CustomLoadableComponent(props: any) {
    return (
      <React.Suspense fallback={<LoadingComponent />}>
        <ErrorBoundary>
          <LazyComponent {...props} />
        </ErrorBoundary>
      </React.Suspense>
    );
  };
}

// Error boundary component to catch render errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Error in component:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center p-4 text-destructive">
          <p>Something went wrong. Please try again.</p>
        </div>
      );
    }

    return this.props.children;
  }
}
